import React, { useEffect, useMemo, useState } from 'react';

function useViewportWidth() {
  const [w, setW] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

// Same safe palette as DEFAULT_COLORS in PortfolioDashboard —
// avoids Total (purple), Invested (orange), Benchmark (cyan), green, red
const SLICE_COLORS = [
  '#f472b6', // pink
  '#c084fc', // violet
  '#60a5fa', // blue
  '#14b8a6', // teal
  '#fde047', // yellow
  '#fb7185', // rose
  '#818cf8', // periwinkle
  '#e879f9', // fuchsia
  '#a78bfa', // lavender
  '#2dd4bf', // turquoise
  '#f9a8d4', // blush
  '#93c5fd', // sky-light
];

const CURRENCY_REGION = {
  USD: 'USD · United States',
  EUR: 'EUR · Europe',
  GBP: 'GBP · United Kingdom',
  CZK: 'CZK · Czech Republic',
  JPY: 'JPY · Japan',
  CAD: 'CAD · Canada',
  AUD: 'AUD · Australia',
  CHF: 'CHF · Switzerland',
  PLN: 'PLN · Poland',
  SEK: 'SEK · Sweden',
  NOK: 'NOK · Norway',
  DKK: 'DKK · Denmark',
  HKD: 'HKD · Hong Kong',
  SGD: 'SGD · Singapore',
  CNY: 'CNY · China',
  KRW: 'KRW · South Korea',
  BRL: 'BRL · Brazil',
  INR: 'INR · India',
};

const ASSET_TYPE_LABEL = {
  Stock: 'Stocks',
  ETF: 'ETFs',
  Crypto: 'Crypto',
  Bond: 'Bonds',
  Commodity: 'Commodities',
  Fund: 'Funds',
  0: 'Stocks',
  1: 'ETFs',
  2: 'Crypto',
  3: 'Bonds',
  4: 'Commodities',
  5: 'Funds',
};

const MAX_HOLDINGS_SHOWN = 12;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSlicePath(cx, cy, outerR, innerR, startAngle, endAngle) {
  // Clamp to avoid degenerate arcs (full-circle case)
  const sweep = Math.min(endAngle - startAngle, 359.9999);
  const end = startAngle + sweep;
  const os = polarToCartesian(cx, cy, outerR, startAngle);
  const oe = polarToCartesian(cx, cy, outerR, end);
  const is = polarToCartesian(cx, cy, innerR, startAngle);
  const ie = polarToCartesian(cx, cy, innerR, end);
  const large = sweep > 180 ? 1 : 0;
  return [
    `M ${os.x} ${os.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${oe.x} ${oe.y}`,
    `L ${ie.x} ${ie.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${is.x} ${is.y}`,
    'Z',
  ].join(' ');
}

function buildGroups(holdings, mode, dividendsByTicker = null) {
  const map = new Map();

  // Dividends mode pulls from a separate ticker → base-currency map (TTM totals)
  if (mode === 'dividends') {
    if (dividendsByTicker) {
      for (const [ticker, value] of Object.entries(dividendsByTicker)) {
        const v = Number(value || 0);
        if (v > 0) map.set(String(ticker).toUpperCase(), v);
      }
    }
  } else {
    holdings.forEach((h) => {
      const value = Number(h.marketValueBase ?? h.holdingsValueBase ?? 0);
      if (value <= 0) return;

      let key;
      if (mode === 'assetType') {
        const raw = h.assetType ?? '';
        key = ASSET_TYPE_LABEL[raw] || String(raw) || 'Other';
      } else if (mode === 'currency') {
        const cur = String(h.currency || '').toUpperCase() || 'Unknown';
        key = CURRENCY_REGION[cur] || cur;
      } else {
        // holdings mode — individual positions
        key = h.ticker ? String(h.ticker).toUpperCase() : (h.displayName || 'Unknown');
      }

      map.set(key, (map.get(key) ?? 0) + value);
    });
  }

  const entries = [...map.entries()].sort(([, a], [, b]) => b - a);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total <= 0) return { slices: [], total: 0 };

  // For holdings/dividends modes: group small slices into "Other"
  let finalEntries = entries;
  if ((mode === 'holdings' || mode === 'dividends') && entries.length > MAX_HOLDINGS_SHOWN) {
    const top = entries.slice(0, MAX_HOLDINGS_SHOWN);
    const otherValue = entries.slice(MAX_HOLDINGS_SHOWN).reduce((s, [, v]) => s + v, 0);
    finalEntries = [...top, ['Other', otherValue]];
  }

  let angle = 0;
  const slices = finalEntries.map(([label, value], i) => {
    const pct = (value / total) * 100;
    const sweep = (pct / 100) * 360;
    const slice = { label, value, pct, color: SLICE_COLORS[i % SLICE_COLORS.length], startAngle: angle, endAngle: angle + sweep };
    angle += sweep;
    return slice;
  });

  return { slices, total };
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

const ALL_TABS = [
  { id: 'holdings', label: 'Holdings' },
  { id: 'currency', label: 'Currency' },
  { id: 'assetType', label: 'Asset Type' },
  { id: 'dividends', label: 'Dividends' },
];

const CX = 110;
const CY = 110;
const OUTER_R = 90;
const INNER_R = 56;
const GAP_DEG = 1.2;

function PortfolioAllocationChart({
  groupedHoldings = [],
  reportCurrency = 'USD',
  loading = false,
  tickerColorMap = null,
  onTickerColorChange = null,
  currencyColors = null,
  onCurrencyColorChange = null,
  assetTypeColors = null,
  onAssetTypeColorChange = null,
  onSliceClick = null,
  portfolioId = null,
  dividendsByTicker = null,
  showDividends = false,
}) {
  const TABS = showDividends ? ALL_TABS : ALL_TABS.filter(t => t.id !== 'dividends');
  const tabStorageKey = portfolioId ? `allocTab-${portfolioId}` : null;
  const [activeTab, setActiveTab] = useState(() => {
    if (!tabStorageKey) return 'holdings';
    try { return localStorage.getItem(tabStorageKey) || 'holdings'; } catch { return 'holdings'; }
  });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const vw = useViewportWidth();

  const { slices, total } = useMemo(() => {
    const result = buildGroups(groupedHoldings, activeTab, dividendsByTicker);
    if ((activeTab === 'holdings' || activeTab === 'dividends') && tickerColorMap && tickerColorMap.size > 0) {
      result.slices = result.slices.map(slice => ({
        ...slice,
        color: tickerColorMap.get(slice.label) || slice.color,
      }));
    } else if (activeTab === 'currency' && currencyColors) {
      result.slices = result.slices.map(slice => ({
        ...slice,
        color: currencyColors[slice.label] || slice.color,
      }));
    } else if (activeTab === 'assetType' && assetTypeColors) {
      result.slices = result.slices.map(slice => ({
        ...slice,
        color: assetTypeColors[slice.label] || slice.color,
      }));
    }
    return result;
  }, [groupedHoldings, activeTab, tickerColorMap, currencyColors, assetTypeColors, dividendsByTicker]);

  const hovered = hoveredIndex !== null ? slices[hoveredIndex] : null;

  if (loading) {
    return (
      <section style={cardStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>Portfolio Allocation</h3>
        </div>
        <p style={emptyStyle}>Loading...</p>
      </section>
    );
  }

  if (!groupedHoldings.length || slices.length === 0) {
    return (
      <section style={cardStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>Portfolio Allocation</h3>
        </div>
        <p style={emptyStyle}>No holdings data yet.</p>
      </section>
    );
  }

  return (
    <section style={cardStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Portfolio Allocation</h3>
      </div>

      {/* Tab bar */}
      <div style={tabBarStyle}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveTab(tab.id);
              setHoveredIndex(null);
              if (tabStorageKey) { try { localStorage.setItem(tabStorageKey, tab.id); } catch {} }
            }}
            style={{
              ...tabButtonStyle,
              ...(activeTab === tab.id ? activeTabButtonStyle : null),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ ...bodyStyle, flexDirection: vw < 640 ? 'column' : 'row', alignItems: vw < 640 ? 'center' : 'flex-start' }}>
        {/* Donut chart */}
        <div style={chartWrapStyle} className="rp-alloc-chart-wrap">
          <svg viewBox="0 0 220 220" style={svgStyle}>
            {slices.map((slice, i) => {
              const gapHalf = slices.length > 1 ? GAP_DEG / 2 : 0;
              const start = slice.startAngle + gapHalf;
              const end = slice.endAngle - gapHalf;
              if (end <= start) return null;

              const isHovered = hoveredIndex === i;
              const isOtherDimmed = hoveredIndex !== null && !isHovered;
              const isClickable = onSliceClick && activeTab === 'holdings' && slice.label !== 'Other';

              return (
                <path
                  key={slice.label + i}
                  d={donutSlicePath(CX, CY, isHovered ? OUTER_R + 5 : OUTER_R, INNER_R, start, end)}
                  fill={slice.color}
                  opacity={isOtherDimmed ? 0.35 : 1}
                  style={{ cursor: isClickable ? 'pointer' : 'default', transition: 'opacity 0.15s, d 0.1s' }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={isClickable ? () => onSliceClick(slice.label, activeTab) : undefined}
                />
              );
            })}

            {/* Center label */}
            {hovered ? (
              <>
                <text x={CX} y={CY - 14} textAnchor="middle" style={centerLabelStyle}>
                  {hovered.label.length > 16 ? hovered.label.slice(0, 14) + '…' : hovered.label}
                </text>
                <text x={CX} y={CY + 6} textAnchor="middle" style={centerValueStyle}>
                  {formatCurrency(hovered.value, reportCurrency)}
                </text>
                <text x={CX} y={CY + 24} textAnchor="middle" style={centerPctStyle}>
                  {hovered.pct.toFixed(1)}%
                </text>
              </>
            ) : activeTab === 'dividends' ? (
              <>
                <text x={CX} y={CY - 14} textAnchor="middle" style={centerLabelStyle}>
                  Forward Income
                </text>
                <text x={CX} y={CY + 6} textAnchor="middle" style={centerTotalStyle}>
                  {formatCurrency(total, reportCurrency)}
                </text>
                <text x={CX} y={CY + 22} textAnchor="middle" style={centerPctStyle}>
                  {slices.length} payer{slices.length !== 1 ? 's' : ''}
                </text>
              </>
            ) : (
              <>
                <text x={CX} y={CY - 6} textAnchor="middle" style={centerLabelStyle}>
                  {slices.length} {activeTab === 'holdings' ? 'positions' : activeTab === 'currency' ? 'currencies' : 'types'}
                </text>
                <text x={CX} y={CY + 14} textAnchor="middle" style={centerTotalStyle}>
                  {formatCurrency(total, reportCurrency)}
                </text>
              </>
            )}
          </svg>
        </div>

        {/* Legend */}
        <div className="rp-alloc-legend" style={legendStyle}>
          {slices.map((slice, i) => {
            const isLegendClickable = onSliceClick && activeTab === 'holdings' && slice.label !== 'Other';
            return (
            <div
              key={slice.label + i}
              style={{
                ...legendItemStyle,
                ...(hoveredIndex === i ? legendItemHoverStyle : null),
                ...(hoveredIndex !== null && hoveredIndex !== i ? legendItemDimStyle : null),
                ...(isLegendClickable ? { cursor: 'pointer' } : {}),
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={isLegendClickable ? () => onSliceClick(slice.label, activeTab) : undefined}
            >
              {(activeTab === 'holdings' && onTickerColorChange) ||
               (activeTab === 'currency' && onCurrencyColorChange) ||
               (activeTab === 'assetType' && onAssetTypeColorChange) ? (
                <div style={{ position: 'relative', flexShrink: 0 }} title={`Click to change color`}>
                  <div style={{ ...legendSwatchStyle, background: slice.color, width: '16px', height: '16px', borderRadius: '4px' }} />
                  <input
                    type="color"
                    value={slice.color}
                    onChange={(e) => {
                      if (activeTab === 'holdings' && onTickerColorChange) onTickerColorChange(slice.label, e.target.value);
                      else if (activeTab === 'currency' && onCurrencyColorChange) onCurrencyColorChange(slice.label, e.target.value);
                      else if (activeTab === 'assetType' && onAssetTypeColorChange) onAssetTypeColorChange(slice.label, e.target.value);
                    }}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }}
                  />
                </div>
              ) : (
                <span style={{ ...legendSwatchStyle, background: slice.color }} />
              )}
              <span style={legendLabelStyle}>{slice.label}</span>
              <span style={legendValueStyle}>
                {formatCurrency(slice.value, reportCurrency)}
              </span>
              <span style={{ ...legendPctStyle, color: slice.color }}>
                {slice.pct.toFixed(1)}%
              </span>
            </div>
          );})}
        </div>
      </div>
    </section>
  );
}

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '20px 24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const headerStyle = {
  marginBottom: '16px',
};

const titleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
};

const tabBarStyle = {
  display: 'flex',
  gap: '8px',
  marginBottom: '20px',
  flexWrap: 'wrap',
};

const tabButtonStyle = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const activeTabButtonStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
  border: '1px solid var(--primary)',
};

const bodyStyle = {
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};

const chartWrapStyle = {
  flexShrink: 0,
  width: '220px',
  height: '220px',
};

const svgStyle = {
  width: '100%',
  height: '100%',
  display: 'block',
  overflow: 'visible',
};

const centerLabelStyle = {
  fill: 'var(--text-secondary)',
  fontSize: '11px',
  fontWeight: 600,
};

const centerValueStyle = {
  fill: 'var(--text-primary)',
  fontSize: '13px',
  fontWeight: 800,
};

const centerPctStyle = {
  fill: 'var(--text-secondary)',
  fontSize: '11px',
  fontWeight: 700,
};

const centerTotalStyle = {
  fill: 'var(--text-primary)',
  fontSize: '12px',
  fontWeight: 700,
};

const legendStyle = {
  flex: 1,
  minWidth: '240px',
  // CSS multi-column so items fill top-to-bottom, then wrap into the next
  // column — no vertical scroll, all positions visible.
  columnWidth: '230px',
  columnGap: '28px',
};

const legendItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 8px',
  borderRadius: '10px',
  cursor: 'default',
  transition: 'opacity 0.15s, background 0.15s',
  breakInside: 'avoid',
  WebkitColumnBreakInside: 'avoid',
};

const legendItemHoverStyle = {
  background: 'var(--surface)',
};

const legendItemDimStyle = {
  opacity: 0.4,
};

const legendSwatchStyle = {
  width: '12px',
  height: '12px',
  borderRadius: '4px',
  flexShrink: 0,
};

const legendLabelStyle = {
  flex: 1,
  color: 'var(--text-primary)',
  fontSize: '13px',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const legendValueStyle = {
  color: 'var(--text-secondary)',
  fontSize: '12px',
  fontWeight: 600,
  flexShrink: 0,
};

const legendPctStyle = {
  fontSize: '12px',
  fontWeight: 800,
  flexShrink: 0,
  minWidth: '42px',
  textAlign: 'right',
};

const emptyStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
};

export default PortfolioAllocationChart;
